import { Link, useForm, usePage } from '@inertiajs/react';
import { FormEventHandler } from 'react';

import AddressAutocomplete from '@/components/address/address-autocomplete';
import InputError from '@/components/input-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TaxRate } from '@/types/TaxRate/taxRate';
import { LoaderCircle } from 'lucide-react';
import type { SharedData } from '@/types';

type CreateForm = {
    address: string;
    city: string;
    zip: string;
};

export default function RateByAddress({ rate }: { rate: null | TaxRate }) {
    const { auth } = usePage<SharedData>().props;

    const { data, setData, post, processing, errors } = useForm<Required<CreateForm>>({
        address: '',
        city: '',
        zip: '',
    });

    const submit: FormEventHandler = (e) => {
        e.preventDefault();
        post(route('home.taxRate'), {
            preserveScroll: true,
            // onFinish: () => reset('address', 'city', 'zip'),
        });
    };

    const handleAddressSelect = (place: google.maps.places.PlaceResult) => {
        let addressString = '';

        place.address_components?.map((addressAutocomplete) => {
            switch (addressAutocomplete.types[0]) {
                case 'street_number':
                    addressString = addressAutocomplete.long_name;
                    break;
                case 'route':
                    addressString += ' ' + addressAutocomplete.long_name;
                    break;
                case 'locality':
                    setData('city', addressAutocomplete.long_name);
                    break;
                case 'postal_code':
                    setData('zip', addressAutocomplete.long_name);
                    break;
            }
        });

        setData('address', addressString);

        return addressString;
    };

    return (
        <>
            {rate?.rate && (
                <div className="mb-5 bg-amber-300 p-2 dark:bg-amber-950">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Sales and Use Tax Rate in "{rate?.address}" is {rate?.rate}%
                    </h3>
                    <p className="text-gray-900 dark:text-white">
                        <span className="text-xs">As of {rate?.syncedAt}</span>
                    </p>
                    {auth.user ? (
                        <p className="text-xl text-gray-900 dark:text-white">
                            Add this location to your {' '}
                            <Link className="cursor-pointer underline" href={route('dashboard')}>
                                <strong>Dashboard</strong>
                            </Link>{' '}
                            and receive tax rate update notifications
                        </p>
                    ) : (
                        <p className="text-xl text-gray-900 dark:text-white">
                            <Link className="cursor-pointer underline" href={route('register')}>
                                {' '}
                                <strong>Sign up</strong>
                            </Link>{' '}
                            and receive tax rate update notifications for this location
                        </p>
                    )}
                </div>
            )}
            <form id="tax-rate-by-address" className="grid auto-rows-min gap-4 " onSubmit={submit}>
                <div className="relative grid auto-rows-min gap-6 md:grid-cols-3">
                    <div className="grid gap-2">
                        <Label htmlFor="address">Address</Label>
                        <AddressAutocomplete
                            address={data.address}
                            setAddress={(address) => setData('address', address)}
                            onSelect={handleAddressSelect}
                        />
                        <InputError message={errors.address} className="mt-2" />
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                            id="city"
                            type="text"
                            required
                            autoComplete="city"
                            value={data.city}
                            onChange={(e) => setData('city', e.target.value)}
                            disabled={processing}
                            placeholder="City"
                        />
                        <InputError message={errors.city} className="mt-2" />
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="zip">Zip</Label>
                        <Input
                            id="zip"
                            type="text"
                            required
                            autoComplete="zip"
                            value={data.zip}
                            onChange={(e) => setData('zip', e.target.value)}
                            disabled={processing}
                            placeholder="Zip"
                        />
                        <InputError message={errors.zip} className="mt-2" />
                    </div>

                    <div className="col-span-full flex justify-center">
                        <Button
                            type="submit"
                            className="mt-2 w-full cursor-pointer rounded-xl bg-gray-200 px-6 py-3 font-semibold text-gray-900 shadow-md transition hover:bg-gray-300"
                            disabled={processing}
                        >
                            {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                            Get tax rate
                        </Button>
                    </div>
                </div>
            </form>
        </>
    );
}
